import React, { useState, useEffect } from "react";
import {
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Trash2,
  Save,
  RefreshCw,
} from "lucide-react";
import {
  presencaService,
  MembroPresenca,
  StatusPresenca,
} from "../services/presencaService";
import { notifyError, notifySuccess } from "../utils/notify";

interface GestaoPresencasProps {
  ataId: string;
  empresaId: string;
  presencasIniciais?: MembroPresenca[];
  onPresencasAtualizadas?: (presencas: MembroPresenca[]) => void;
  readonly?: boolean;
}

export const GestaoPresencas: React.FC<GestaoPresencasProps> = ({
  ataId,
  empresaId,
  presencasIniciais = [],
  onPresencasAtualizadas,
  readonly = false,
}) => {
  const [presencas, setPresencas] =
    useState<MembroPresenca[]>(presencasIniciais);
  const [loading, setLoading] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [novoMembro, setNovoMembro] = useState({
    nome: "",
    cargo: "",
    tipo: "convidado",
    status: "presente" as StatusPresenca,
    justificativa: "",
  });

  // Load existing presences
  useEffect(() => {
    const carregarPresencas = async () => {
      try {
        setLoading(true);
        const data = await presencaService.obterPresencasPorAta(ataId);
        setPresencas(data);
      } catch (error) {
        console.error("Erro ao carregar presenças:", error);
      } finally {
        setLoading(false);
      }
    };

    if (ataId) {
      carregarPresencas();
    }
  }, [ataId]);



  const gerarListaInicial = async () => {
    try {
      setLoading(true);
      const lista = await presencaService.gerarListaPresencaInicial(
        empresaId,
        ataId,
      );
      const novasPresencas = await presencaService.registrarPresencas(lista);
      setPresencas(novasPresencas);
      onPresencasAtualizadas?.(novasPresencas);
      notifySuccess("Lista de presença gerada com sucesso!");
    } catch (error) {
      console.error("Erro ao gerar lista:", error);
      notifyError("Erro ao gerar lista de presença");
    } finally {
      setLoading(false);
    }
  };

  const atualizarStatus = async (
    presencaId: string,
    novoStatus: StatusPresenca,
  ) => {
    if (readonly) return;

    try {
      const presencaAtualizada = await presencaService.atualizarPresenca(
        presencaId,
        { status: novoStatus },
      );

      setPresencas((prev) =>
        prev.map((p) => (p.id === presencaId ? presencaAtualizada : p)),
      );
      onPresencasAtualizadas?.(
        presencas.map((p) => (p.id === presencaId ? presencaAtualizada : p)),
      );
      notifySuccess("Status atualizado!");
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      notifyError("Erro ao atualizar status");
    }
  };

  const atualizarJustificativa = async (
    presencaId: string,
    justificativa: string,
  ) => {
    if (readonly) return;

    try {
      const presencaAtualizada = await presencaService.atualizarPresenca(
        presencaId,
        { justificativa },
      );

      setPresencas((prev) =>
        prev.map((p) => (p.id === presencaId ? presencaAtualizada : p)),
      );
      onPresencasAtualizadas?.(
        presencas.map((p) => (p.id === presencaId ? presencaAtualizada : p)),
      );
    } catch (error) {
      console.error("Erro ao atualizar justificativa:", error);
    }
  };

  const adicionarMembroExterno = async () => {
    if (!novoMembro.nome || !novoMembro.cargo) {
      notifyError("Preencha nome e cargo do membro");
      return;
    }

    try {
      const novaPresenca: Omit<
        MembroPresenca,
        "id" | "created_at" | "updated_at"
      > = {
        ata_id: ataId,
        membro_id: null,
        nome_membro: novoMembro.nome,
        cargo: novoMembro.cargo,
        tipo_membro: novoMembro.tipo,
        status: novoMembro.status,
        justificativa: novoMembro.justificativa || undefined,
      };

      const [criada] = await presencaService.registrarPresencas([novaPresenca]);
      setPresencas((prev) => [...prev, criada]);
      onPresencasAtualizadas?.([...presencas, criada]);

      // Reset form
      setNovoMembro({
        nome: "",
        cargo: "",
        tipo: "convidado",
        status: "presente",
        justificativa: "",
      });
      setMostrarFormulario(false);
      notifySuccess("Membro adicionado!");
    } catch (error) {
      console.error("Erro ao adicionar membro:", error);
      notifyError("Erro ao adicionar membro");
    }
  };

  const excluirPresenca = async (id: string) => {
    if (readonly) return;

    try {
      await presencaService.excluirPresenca(id);
      const novasPresencas = presencas.filter((p) => p.id !== id);
      setPresencas(novasPresencas);
      onPresencasAtualizadas?.(novasPresencas);
      notifySuccess("Presença removida!");
    } catch (error) {
      console.error("Erro ao excluir presença:", error);
      notifyError("Erro ao remover presença");
    }
  };

  const quorum = presencaService.calcularQuorum(presencas);

  const getStatusIcon = (status: StatusPresenca) => {
    switch (status) {
      case "presente":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "ausente":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "justificado":
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: StatusPresenca) => {
    switch (status) {
      case "presente":
        return "bg-green-50 border-green-200";
      case "ausente":
        return "bg-red-50 border-red-200";
      case "justificado":
        return "bg-yellow-50 border-yellow-200";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2">Carregando presenças...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with quorum info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="font-semibold text-gray-800">Lista de Presença</h3>
              <p className="text-sm text-gray-600">
                {quorum.total} membros • {quorum.presentes} presentes •{" "}
                {quorum.ausentes} ausentes • {quorum.justificados} justificados
              </p>
            </div>
          </div>

          <div className="text-right">
            <div
              className={`text-2xl font-bold ${quorum.quorumAtingido ? "text-green-600" : "text-red-600"}`}
            >
              {quorum.percentualPresentes.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-600">
              {quorum.quorumAtingido ? "✓ Quórum atingido" : "✗ Sem quórum"}
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {!readonly && (
        <div className="flex gap-2">
          {presencas.length === 0 && (
            <button
              onClick={gerarListaInicial}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Gerar Lista de Membros
            </button>
          )}
          <button
            onClick={() => setMostrarFormulario(!mostrarFormulario)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Adicionar Convidado
          </button>
        </div>
      )}

      {/* Add external member form */}
      {mostrarFormulario && !readonly && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold mb-3">Adicionar Membro Externo</h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Nome"
              value={novoMembro.nome}
              onChange={(e) =>
                setNovoMembro({ ...novoMembro, nome: e.target.value })
              }
              className="px-3 py-2 border rounded"
            />
            <input
              type="text"
              placeholder="Cargo"
              value={novoMembro.cargo}
              onChange={(e) =>
                setNovoMembro({ ...novoMembro, cargo: e.target.value })
              }
              className="px-3 py-2 border rounded"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={novoMembro.status}
              onChange={(e) =>
                setNovoMembro({
                  ...novoMembro,
                  status: e.target.value as StatusPresenca,
                })
              }
              className="px-3 py-2 border rounded"
            >
              <option value="presente">Presente</option>
              <option value="ausente">Ausente</option>
              <option value="justificado">Justificado</option>
            </select>
            <button
              onClick={adicionarMembroExterno}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Save className="w-4 h-4" />
              Salvar
            </button>
            <button
              onClick={() => setMostrarFormulario(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Presences list */}
      <div className="space-y-2">
        {presencas.map((presenca) => (
          <div
            key={presenca.id}
            className={`border rounded-lg p-4 ${getStatusColor(presenca.status)}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {getStatusIcon(presenca.status)}
                <div className="flex-1">
                  <div className="font-semibold text-gray-800">
                    {presenca.nome_membro}
                  </div>
                  <div className="text-sm text-gray-600">
                    {presenca.cargo} • {presenca.tipo_membro}
                  </div>
                </div>
              </div>

              {!readonly && (
                <div className="flex items-center gap-2">
                  <select
                    value={presenca.status}
                    onChange={(e) =>
                      atualizarStatus(
                        presenca.id!,
                        e.target.value as StatusPresenca,
                      )
                    }
                    className="px-3 py-1 border rounded text-sm"
                  >
                    <option value="presente">Presente</option>
                    <option value="ausente">Ausente</option>
                    <option value="justificado">Justificado</option>
                  </select>
                  {!presenca.membro_id && (
                    <button
                      onClick={() => excluirPresenca(presenca.id!)}
                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                      title="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {presenca.status !== "presente" && (
              <div className="mt-3">
                <textarea
                  placeholder="Justificativa (opcional)"
                  value={presenca.justificativa || ""}
                  onChange={(e) =>
                    atualizarJustificativa(presenca.id!, e.target.value)
                  }
                  disabled={readonly}
                  className="w-full px-3 py-2 border rounded text-sm resize-none"
                  rows={2}
                />
              </div>
            )}
          </div>
        ))}

        {presencas.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhuma presença registrada</p>
            <p className="text-sm">
              Clique em "Gerar Lista de Membros" para começar
            </p>
          </div>
        )}
      </div>

      {/* Warning if no quorum */}
      {presencas.length > 0 && !quorum.quorumAtingido && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertCircle className="w-5 h-5" />
            <div>
              <p className="font-semibold">Atenção: Quórum não atingido</p>
              <p className="text-sm">
                São necessários pelo menos {Math.floor(quorum.total / 2) + 1}{" "}
                membros presentes para validar a reunião (NR-05).
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
